import {
  CurrentReceiptData,
  CurrentReceiptItem,
} from '../../LoyaltyPage/types';
import { distributeClientBonus } from '../../LoyaltyPage/helpers';

export const COUPONS_DEV_TAG_ID = 1066;
export const COUPONS_PROD_TAG_ID = 1202;
export const couponsTagEnabled = (
  isDev: boolean,
  sourceTags?: { tag_id: number }[]
) =>
  !!sourceTags?.some(
    (tag) => tag.tag_id === (isDev ? COUPONS_DEV_TAG_ID : COUPONS_PROD_TAG_ID)
  );
export const loyaltyV3TagEnabled = (
  isDev: boolean,
  sourceTags?: { tag_id: number }[]
) =>
  !!sourceTags?.some((tag) => tag.tag_id === (isDev ? 1064 : 1205)) ||
  couponsTagEnabled(isDev, sourceTags);
export interface CouponQuote {
  status: string;
  availability: string;
  doc_id: string;
  ware_id: string;
  price: number;
  price_with_discount: number;
  discount: number;
}
export interface CheckedCoupon {
  code: string;
  quote: CouponQuote;
  receipt: CurrentReceiptData;
  fingerprint: string;
}
export const couponAvailabilityError = (availability: string) => {
  if (availability === 'Available') return null;
  return [
    'NotExist',
    'Expired',
    'NotStarted',
    'ExceededMaxUsages',
    'WrongIntegration',
    'ProductNotInCheck',
    'InvalidQuantity',
    'CheckAlreadyHasCoupon',
  ].includes(availability)
    ? availability
    : 'INVALID_RESPONSE';
};
export const normaliseCouponCode = (code: string) => code.trim();
// Preserve case until the backend's case sensitivity is documented.
export const isCouponCode = (code: string) =>
  /^[A-Za-z0-9]{6}$/.test(code) || /^S_\d{4}$/.test(code);
// Temporary compatibility for dev backend coupons such as S_4100.
// Keep the normal six-character format and do not strip the underscore.
export const isCouponEntry = (code: string) =>
  isCouponCode(code) && /[A-Za-z]/.test(code);
export const couponOrderPhone = (_isDev: boolean, value?: string) => {
  const phone = value?.trim().replace(/^\+/, '');
  return /^7\d{10}$/.test(phone ?? '') ? phone : undefined;
};
export const money = (value: number) => Math.round(value * 100) / 100;
export const receiptFingerprint = (receipt: CurrentReceiptData) =>
  JSON.stringify([
    receipt.doc_id,
    receipt.items_cover_percent,
    receipt.items_check_percent,
    receipt.pharmacist_check_percent,
    receipt.items.map((item) => [
      item.ware_id,
      item.quantity,
      item.part_quantity,
      item.base_price,
      item.total_price,
      item.bonus_percent,
      item.stada_bonus_percent,
    ]),
  ]);
export const couponReceiptError = (receipt: CurrentReceiptData | null) => {
  if (!receipt?.items?.length) return 'NO_RECEIPT';
  if (receipt.items.length !== 1 || Number(receipt.items[0].quantity) !== 1)
    return 'ONE_ITEM_ONLY';
  const item = receipt.items[0];
  if (item.part_quantity != null && Number(item.part_quantity) !== 1)
    return 'ONE_ITEM_ONLY';
  if (
    !Number.isFinite(item.base_price) ||
    !Number.isFinite(item.total_price) ||
    item.base_price <= 0 ||
    item.total_price <= 0
  )
    return 'INVALID_PRICE';
  return null;
};
export const quoteError = (receipt: CurrentReceiptData, quote: CouponQuote) => {
  const receiptError = couponReceiptError(receipt);
  if (receiptError) return receiptError;
  const availabilityError = couponAvailabilityError(quote.availability);
  if (availabilityError) return availabilityError;
  const item = receipt.items[0];
  if (
    String(quote.doc_id) !== String(receipt.doc_id) ||
    quote.ware_id !== item.ware_id
  )
    return 'RECEIPT_CHANGED';
  if (
    ![quote.price, quote.discount, quote.price_with_discount].every(
      Number.isFinite
    ) ||
    quote.price_with_discount <= 0 ||
    quote.discount <= 0 ||
    money(quote.price - quote.discount) !== money(quote.price_with_discount)
  )
    return 'INVALID_RESPONSE';
  if (
    money(quote.price) !== money(item.base_price) ||
    money(item.total_price) !== money(item.base_price)
  )
    return 'RECEIPT_CHANGED';
  return null;
};
export const couponReceipt = (checked: CheckedCoupon): CurrentReceiptData => {
  const price = checked.quote.price_with_discount;
  const cover = Math.max(
    0,
    Math.min(30, checked.receipt.items_cover_percent ?? 0)
  );
  return {
    ...checked.receipt,
    items: checked.receipt.items.map((item) => ({
      ...item,
      base_price: price,
      unit_price: price,
      total_price: price,
      maxBonusDiscount: Math.max(
        0,
        Math.min(Math.floor((price * cover) / 100), Math.floor(price - 1))
      ),
    })),
  };
};
export const couponAmounts = (
  checked: CheckedCoupon,
  balance: number,
  useBonuses: boolean,
  partnerPercent: number,
  stadaBalance = 0
) => {
  const items = distributeClientBonus(
    couponReceipt(checked).items,
    useBonuses ? balance : 0,
    partnerPercent,
    useBonuses ? stadaBalance : 0
  );
  const bonus = items.reduce((sum, item) => sum + item.appliedBonus, 0);
  return {
    original: checked.quote.price,
    coupon: checked.quote.discount,
    afterCoupon: checked.quote.price_with_discount,
    bonus,
    payable: money(checked.quote.price_with_discount - bonus),
    items,
  };
};
export const standartItems = (items: CurrentReceiptItem[]) =>
  items.map((item) => ({
    ware_id: item.ware_id,
    price: money(item.base_price - item.appliedBonus / item.quantity),
    quantity: item.quantity,
  }));
export const confirmationPayload = (
  checked: CheckedCoupon,
  sourceCode: string,
  items: CurrentReceiptItem[],
  phone?: string,
  bonusCode?: string,
  couponOnlyPhone?: string
) => ({
  doc_id: checked.receipt.doc_id,
  source_code: sourceCode,
  coupon_code: checked.code,
  // The approved coupon-only account is an order owner, not a PL login.
  // Bonus fields below intentionally still depend on the real PL client.
  ...(phone || couponOnlyPhone ? { phone: phone || couponOnlyPhone } : {}),
  ...(phone && bonusCode ? { confirm_code: bonusCode } : {}),
  bonus_used: !!(phone && bonusCode),
  items: items.map((item) => ({
    ...standartItems([item])[0],
    name: item.name,
  })),
  client_bonus_debit: phone
    ? items.reduce(
        (sum, item) => sum + item.appliedBonus - (item.appliedStadaBonus ?? 0),
        0
      )
    : 0,
  client_bonus_credit: phone
    ? items.reduce((sum, item) => sum + item.bonusesToClient, 0)
    : 0,
  pharmacist_bonus_credit: phone
    ? items.reduce((sum, item) => sum + item.bonusesToPharmacist, 0)
    : 0,
  stada_bonus_credit: phone
    ? items.reduce((sum, item) => sum + item.stadaBonus, 0)
    : 0,
  stada_bonus_debit: phone
    ? items.reduce((sum, item) => sum + (item.appliedStadaBonus ?? 0), 0)
    : 0,
});
